import { useState, useEffect, useRef } from 'react'
import { Card, Flex, Text, Button, Box, Progress, Badge, Dialog, TextField } from '@radix-ui/themes'
import { Upload, CheckCircle2, FileText, XCircle, ChevronUp, Eye, Edit } from 'lucide-react'
import axios from 'axios'
import { getToken } from '@/utils/utils'
import { makeUrlTransferDocuments, makeUrlTransfer } from '@/hooks/connectionData'
import { TransferDocuments, DocumentAnalysis } from '@/interfaces/Transfer'
import { TransferDocumentPreview } from './TransferDocumentPreview'

interface DocumentSlot {
  key: keyof TransferDocuments
  label: string
  required: boolean
}

const DOCUMENT_SLOTS: DocumentSlot[] = [
  { key: 'frontBuyerId', label: 'Cédula comprador (frontal)', required: true },
  { key: 'backBuyerId', label: 'Cédula comprador (reverso)', required: true },
  { key: 'frontSellerId', label: 'Cédula vendedor (frontal)', required: true },
  { key: 'backSellerId', label: 'Cédula vendedor (reverso)', required: true },
  { key: 'draftContract', label: 'Borrador contrato (PDF)', required: true },
  { key: 'circulationPermit', label: 'Permiso de circulación', required: true },
  { key: 'powerDocument', label: 'Poder notarial (si aplica)', required: false },
]

interface TransferDocumentUploadProps {
  transferId: string
  organizationId: string
  documents: TransferDocuments
  documentAnalysis?: DocumentAnalysis
  onUploadComplete: () => void
  disabled?: boolean
  sellerType?: 'Natural' | 'Legal'
}

export const TransferDocumentUpload = ({
  transferId,
  organizationId,
  documents = {},
  documentAnalysis,
  onUploadComplete,
  disabled = false,
  sellerType = 'Natural',
}: TransferDocumentUploadProps) => {
  const [uploading, setUploading] = useState<string | null>(null)
  const [uploadProgress, setUploadProgress] = useState<number>(0)
  const [error, setError] = useState<string | null>(null)
  const [expandedDoc, setExpandedDoc] = useState<string | null>(null)

  // Multi Upload Modal State
  const [showMultiModal, setShowMultiModal] = useState(false)
  const [expectedFiles, setExpectedFiles] = useState<number | null>(null)
  const [stagedFile1, setStagedFile1] = useState<File | null>(null)
  const [stagedFile2, setStagedFile2] = useState<File | null>(null)

  // Validation Modal State
  const [showValidation, setShowValidation] = useState(false)
  const [validationData, setValidationData] = useState({
    powerDate: '',
    notaryName: '',
    notaryOffice: '',
    documentType: 'Escritura Pública',
  })
  const [savingValidation, setSavingValidation] = useState(false)
  const prevProcessingRef = useRef<Record<string, boolean>>({})
  const [localProcessingSlot, setLocalProcessingSlot] = useState<string | null>(null)

  // Ensure documents is not null (default param only handles undefined)
  const safeDocuments = documents || {}

  const openValidationModal = (extractedData: any) => {
    setValidationData({
      powerDate: extractedData?.powerDate || '',
      notaryName: extractedData?.notaryName || '',
      notaryOffice: extractedData?.notaryOffice || '',
      documentType: 'Escritura Pública',
    })
    setShowValidation(true)
  }

  useEffect(() => {
    // 1. Clear localProcessingSlot if the DB has started or finished processing it
    if (localProcessingSlot) {
      const docKey = localProcessingSlot as keyof TransferDocuments
      const localAnalysis = documentAnalysis?.[docKey] as any
      // If DB took over (isProcessing) or already finished (has results)
      if (localAnalysis && (localAnalysis.isProcessing || localAnalysis.extractedData || localAnalysis.validation !== undefined || localAnalysis.error || localAnalysis.individualResults)) {
        
        // If it finished without us ever seeing isProcessing = true in a previous render
        if (!localAnalysis.isProcessing && !prevProcessingRef.current[localProcessingSlot]) {
          if (localAnalysis.error) {
            alert(`Ocurrió un error al procesar el documento.`)
          } else {
            setExpandedDoc(localProcessingSlot)
            if (localProcessingSlot === 'powerDocument') {
              const results = localAnalysis.individualResults || (localAnalysis.extractedData ? [{ extractedData: localAnalysis.extractedData }] : [])
              const toValidate = results.find((r: any) => r.extractedData?.documentType !== 'Certificado de Vigencia')
              if (toValidate) {
                openValidationModal(toValidate.extractedData)
              }
            }
          }
        }
        
        setLocalProcessingSlot(null)
      }
    }

    // 2. Handle transitions from processing -> done for all documents
    Object.keys(documentAnalysis || {}).forEach((key) => {
      const analysis = (documentAnalysis as any)[key]
      const isProcessingDb = analysis?.isProcessing
      const wasProcessing = prevProcessingRef.current[key]

      if (wasProcessing && !isProcessingDb) {
        // Just finished processing
        if (analysis.error) {
          alert(`Ocurrió un error al procesar el documento.`)
        } else {
          setExpandedDoc(key)
          
          if (key === 'powerDocument') {
            const results = analysis.individualResults || (analysis.extractedData ? [{ extractedData: analysis.extractedData }] : [])
            const toValidate = results.find((r: any) => r.extractedData?.documentType !== 'Certificado de Vigencia')
            if (toValidate) {
              openValidationModal(toValidate.extractedData)
            }
          }
        }
      }

      prevProcessingRef.current[key] = isProcessingDb
    })
  }, [documentAnalysis, localProcessingSlot])

  // Active polling while any document is processing
  const isProcessingAny = Object.values(documentAnalysis || {}).some((a: any) => a?.isProcessing)
  const isLocked = uploading || isProcessingAny || !!localProcessingSlot;
  
  // Use a ref for onUploadComplete to avoid resetting the interval if the function reference changes
  const onUploadCompleteRef = useRef(onUploadComplete)
  useEffect(() => {
    onUploadCompleteRef.current = onUploadComplete
  }, [onUploadComplete])

  useEffect(() => {
    let intervalId: NodeJS.Timeout;
    if (isProcessingAny || !!localProcessingSlot) {
      intervalId = setInterval(() => {
        onUploadCompleteRef.current()
      }, 3000)
    }
    return () => {
      if (intervalId) clearInterval(intervalId)
    }
  }, [isProcessingAny, localProcessingSlot])

  const handleSaveValidation = async () => {
    setSavingValidation(true)
    try {
      const token = getToken()
      const apiUrl = makeUrlTransfer(transferId)

      await axios.patch(
        apiUrl,
        { legalEntity: validationData },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      )

      setShowValidation(false)
      onUploadComplete() // Refresh parent
    } catch (err: any) {
      console.error('Save validation error:', err)
      alert('Error al guardar datos: ' + (err.response?.data?.error || err.message))
    } finally {
      setSavingValidation(false)
    }
  }

  const handleDelete = async (documentType: keyof TransferDocuments) => {
    if (!window.confirm('¿Eliminar este documento?')) return

    try {
      const token = getToken()
      const apiUrl = makeUrlTransferDocuments()

      await axios.delete(apiUrl, {
        headers: { Authorization: `Bearer ${token}` },
        data: { transferId, documentType, organizationId },
      })
      onUploadComplete()
    } catch (error) {
      console.error('Error deleting document:', error)
      alert('Error al eliminar documento')
    }
  }

  const slots: DocumentSlot[] = [
    ...DOCUMENT_SLOTS.map((s) => {
      let updatedSlot = { ...s }
      if (sellerType === 'Legal') {
        if (s.key === 'powerDocument') {
          updatedSlot.required = true
          updatedSlot.label = 'Documento Legal'
        }
        if (s.key === 'frontSellerId') {
          updatedSlot.label = 'E-RUT Empresa (Frontal)'
        }
        if (s.key === 'backSellerId') {
          updatedSlot.label = 'RUT Representante Legal (frontal)'
        }
      } else {
        if (s.key === 'powerDocument') {
          updatedSlot.required = false
          updatedSlot.label = 'Poder notarial (si aplica)'
        }
      }
      return updatedSlot
    }),
  ]

  if (sellerType === 'Legal') {
    const backSellerIdIndex = slots.findIndex((s) => s.key === 'backSellerId')
    if (backSellerIdIndex !== -1) {
      slots.splice(backSellerIdIndex + 1, 0, {
        key: 'backSellerRepId',
        label: 'Cédula Representante Legal (reverso)',
        required: true,
      } as any)
    }
  }


  const handleUpload = async (files: FileList | File[], documentType: keyof TransferDocuments) => {
    setUploading(documentType)
    setUploadProgress(0)
    setError(null)

    try {
      const token = getToken()
      const formData = new FormData()
      Array.from(files).forEach((file) => {
        formData.append('images', file)
      })
      formData.append('transferId', transferId)
      formData.append('documentType', documentType)
      formData.append('organizationId', organizationId)

      const apiUrl = makeUrlTransferDocuments()

      await axios.post(apiUrl, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total)
            setUploadProgress(percentCompleted)
          }
        },
      })

      setLocalProcessingSlot(documentType)
      onUploadComplete()
      setUploadProgress(0) // Reset on success

      if (documentType === 'powerDocument') {
        // Handled by SSE observing isProcessing
      }
    } catch (err: any) {
      console.error('Upload error:', err)
      const errorMessage = err.response?.data?.error || err.message || 'Error al subir documento'
      setError(errorMessage)
      // Do not reset progress on error so user sees it failed at X%
    } finally {
      setUploading(null)
      // Only reset progress if successful (error is null)
      if (!error) {
        setUploadProgress(0)
      }
    }
  }

  const handleConfirmMultiUpload = () => {
    const filesToUpload: File[] = []
    if (stagedFile1) filesToUpload.push(stagedFile1)
    if (stagedFile2) filesToUpload.push(stagedFile2)

    if (filesToUpload.length !== expectedFiles) {
      alert(`Por favor seleccione los ${expectedFiles} archivos requeridos.`)
      return
    }

    handleUpload(filesToUpload, 'powerDocument')
    setShowMultiModal(false)
  }

  return (
    <Card style={{ position: 'relative', overflow: 'hidden' }}>
      {isLocked && (
        <Flex
          align="center"
          justify="center"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255,255,255,0.8)',
            zIndex: 9999,
            backdropFilter: 'blur(4px)',
          }}
        >
          <Flex direction="column" align="center" gap="3" className="bg-white p-6 rounded-lg shadow-xl border border-gray-200">
            <Text size="4" weight="bold" color="blue">
              {uploading ? 'Subiendo documento...' : 'Procesando'}
            </Text>
            <Text size="2" color="gray">
              {uploading 
                ? 'Por favor espere mientras transferimos el archivo.' 
                : 'Analizando el documento. Esto puede tomar unos minutos, por favor no cierre ni recargue la página.'}
            </Text>
            {!uploading && <Progress value={null} />}
          </Flex>
        </Flex>
      )}

      <Flex direction="column" gap="3" p="3">
        <Flex justify="between" align="center">
          <Text size="3" weight="bold">
            Documentos
          </Text>
          <TransferDocumentPreview
            transferId={transferId}
            organizationId={organizationId}
            documents={safeDocuments}
            onUpdate={onUploadComplete}
            sellerType={sellerType}
          />
        </Flex>

        {error && (
          <Box className="bg-red-50 border border-red-200 rounded-md p-2">
            <Text size="2" color="red">
              {error}
            </Text>
          </Box>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {slots.map((slot) => {
            const isUploaded = !!safeDocuments[slot.key]
            const isUploading = uploading === slot.key
            const analysis = documentAnalysis?.[slot.key]
            const validation = analysis?.validation
            const isExpanded = expandedDoc === slot.key

            return (
              <Flex
                key={slot.key}
                direction="column"
                className={`border rounded-md p-3 gap-2 ${
                  isUploaded && validation
                    ? validation.passed
                      ? 'border-green-300 bg-green-50/30'
                      : 'border-red-300 bg-red-50/30'
                    : ''
                }`}
              >
                <Flex align="center" justify="between">
                  <Flex gap="2" align="center">
                    {isUploaded ? (
                      analysis?.isProcessing ? (
                        <Flex gap="2" align="center">
                          <CheckCircle2 className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          <Badge color="blue" size="1">Procesando...</Badge>
                        </Flex>
                      ) : validation ? (
                        validation.passed ? (
                          <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
                        ) : (
                          <div title={!disabled ? 'Eliminar documento' : undefined}>
                            <XCircle
                              className={`w-4 h-4 text-red-500 flex-shrink-0 ${
                                !disabled ? 'cursor-pointer hover:text-red-700 hover:scale-110 transition-all' : ''
                              }`}
                              onClick={(e) => {
                                if (!disabled) {
                                  e.preventDefault()
                                  handleDelete(slot.key)
                                }
                              }}
                            />
                          </div>
                        )
                      ) : validation === undefined && Object.keys(analysis || {}).length > 0 ? (
                        // No validation required for this doc, but AI processed it
                        <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
                      ) : validation === null ? (
                        // Validation was skipped (e.g. Natural to Natural transfer powerDocument)
                        <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      ) : (
                        // Standard fallback for successfully uploaded file without AI analysis yet
                        <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
                      )
                    ) : (
                      <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    )}
                    <Text size="2">
                      {slot.label}
                      {slot.required && !isUploaded && <Text color="red"> *</Text>}
                    </Text>
                  </Flex>

                  <Flex gap="1" align="center">
                    {analysis && !analysis.isProcessing && (
                      <Button variant="ghost" size="1" onClick={() => setExpandedDoc(isExpanded ? null : slot.key)}>
                        {isExpanded ? <ChevronUp className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                      </Button>
                    )}
                    {!disabled &&
                      !isUploading &&
                      (slot.key === 'powerDocument' ? (
                        <Button
                          variant="soft"
                          size="1"
                          onClick={() => {
                            setExpectedFiles(2)
                            setStagedFile1(null)
                            setStagedFile2(null)
                            setShowMultiModal(true)
                          }}
                        >
                          <Upload className="w-3 h-3 mr-1" />
                          {isUploaded ? 'Reemplazar' : 'Subir'}
                        </Button>
                      ) : (
                        <label className="cursor-pointer">
                          <Button variant="soft" size="1" asChild>
                            <span>
                              <Upload className="w-3 h-3" />
                              {isUploaded ? 'Reemplazar' : 'Subir'}
                            </span>
                          </Button>
                          <input
                            type="file"
                            className="hidden"
                            accept=".pdf,.jpg,.jpeg,.png"
                            onChange={(e) => {
                              const files = e.target.files
                              if (files && files.length > 0) {
                                handleUpload(Array.from(files), slot.key)
                              }
                            }}
                          />
                        </label>
                      ))}
                  </Flex>
                </Flex>

                {/* Validation checks summary */}
                {isUploaded && validation && validation.checks.length > 0 && (
                  <Flex direction="column" gap="2">
                    {validation.checks.map((check, i) => (
                      <Flex key={i} direction="column" gap="1">
                        <Flex gap="2" align="center">
                          {check.found ? (
                            <Badge color="green" size="1">
                              OK
                            </Badge>
                          ) : (
                            <Badge color="red" size="1">
                              NO
                            </Badge>
                          )}
                          <Text size="1" color="gray">
                            {check.field}: <strong>{!check.found && check.field === 'Vencimiento Cédula' ? 'Vencida' : check.expected}</strong>
                          </Text>
                        </Flex>
                        {check.actionable && check.extractedValue && !disabled && (
                          <Box className="ml-7 mt-1 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs">
                            <Text size="1" color="gray" className="mb-1 block">
                              Encontramos: <strong>{check.extractedValue}</strong> en el documento.
                            </Text>
                            <Button 
                              size="1" 
                              color="amber" 
                              variant="soft" 
                              onClick={async () => {
                                if (window.confirm(`¿Desea actualizar el ${check.field} de este vehículo a ${check.extractedValue}?`)) {
                                  try {
                                    const token = getToken()
                                    await axios.patch(
                                      makeUrlTransfer(transferId),
                                      { vehicleChassis: check.extractedValue, organizationId },
                                      { headers: { Authorization: `Bearer ${token}` } }
                                    )
                                    onUploadComplete() // Reload the transfer
                                  } catch (err: any) {
                                    alert('Error al actualizar datos: ' + (err.response?.data?.error || err.message))
                                  }
                                }
                              }}
                            >
                              Actualizar Datos Internos
                            </Button>
                          </Box>
                        )}
                      </Flex>
                    ))}
                  </Flex>
                )}

                {/* ALWAYS VISIBLE EXTRACTED DATA */}
                {((analysis?.individualResults && analysis.individualResults.length > 0) 
                  ? analysis.individualResults 
                  : (analysis?.extractedData ? [{ extractedData: analysis.extractedData, originalName: 'Documento' }] : [])
                ).map((result: any, resultIdx: number) => {
                  if (!result.extractedData) return null;
                  return (
                    <Box key={resultIdx} className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded">
                      <Flex justify="between" align="start">
                        <Box>
                          <Text size="1" weight="bold" color="blue" className="mb-1 block">
                            Datos Extraídos {analysis?.individualResults && analysis.individualResults.length > 1 ? `(${result.originalName})` : ''}:
                          </Text>
                          <Flex direction="column" gap="1">
                            {slot.key === 'powerDocument' ? (
                              result.extractedData.documentType === 'Certificado de Vigencia' ? (
                                <>
                                  <Text size="1">
                                    Tipo: <strong>{result.extractedData.documentType}</strong>
                                  </Text>
                                  {result.extractedData.companyName && (
                                    <Text size="1">
                                      Razón Social: <strong>{result.extractedData.companyName}</strong>
                                    </Text>
                                  )}
                                  {result.extractedData.representatives && result.extractedData.representatives.length > 0 && (
                                    <Text size="1">
                                      Representante(s): <strong>{result.extractedData.representatives.join(', ')}</strong>
                                    </Text>
                                  )}
                                </>
                              ) : (
                                <>
                                  <Text size="1">
                                    Tipo: <strong>{result.extractedData.documentType || 'Escritura Pública'}</strong>
                                  </Text>
                                  <Text size="1">
                                    Fecha: <strong>{result.extractedData.powerDate || 'No detectada'}</strong>
                                  </Text>
                                  <Text size="1">
                                    Notario: <strong>{result.extractedData.notaryName || 'No detectado'}</strong>
                                  </Text>
                                  <Text size="1">
                                    Oficina: <strong>{result.extractedData.notaryOffice || 'No detectada'}</strong>
                                  </Text>
                                </>
                              )
                            ) : (
                              <>
                                {Object.entries(result.extractedData).map(([k, v]) => {
                                  if (typeof v === 'object' || k === 'extractionContext') return null;
                                  let label = k;
                                  if (k === 'documentType') label = 'Tipo';
                                  if (k === 'expiryDate') label = 'Vencimiento';
                                  if (k === 'chassis') label = 'Chasis';
                                  if (k === 'plate') label = 'Patente';
                                  if (k === 'rut') label = 'RUT';
                                  return (
                                    <Text key={k} size="1">
                                      {label}: <strong>{String(v)}</strong>
                                    </Text>
                                  );
                                })}
                              </>
                            )}
                          </Flex>
                        </Box>
                        {slot.key === 'powerDocument' && result.extractedData.documentType !== 'Certificado de Vigencia' && (
                          <Button size="1" onClick={() => openValidationModal(result.extractedData)}>
                            <Edit className="w-3 h-3" />
                            Validar Datos
                          </Button>
                        )}
                      </Flex>
                    </Box>
                  );
                })}

                {isUploading && (
                  <Flex direction="column" gap="1">
                    <Progress value={uploadProgress} />
                    <Text size="1" color="gray" align="right">
                      {uploadProgress}%
                    </Text>
                  </Flex>
                )}

                {/* Expanded: show all OCR lines */}
                {isExpanded && analysis && (
                  <Box className="bg-gray-50 rounded p-2 max-h-96 overflow-y-auto border border-gray-200">
                    
                    <Text size="1" weight="bold" color="gray" className="mb-1 block">
                      Texto extraído ({analysis.lines.length} líneas):
                    </Text>
                    {analysis.lines.length > 0 ? (
                      analysis.lines.map((line, i) => {
                        const isMatchedLine = validation?.checks.some((c) => c.matchedLine === line)
                        return (
                          <Text
                            key={i}
                            size="1"
                            className={`block ${isMatchedLine ? 'bg-yellow-100 font-medium' : ''}`}
                          >
                            {line}
                          </Text>
                        )
                      })
                    ) : (
                      <Text size="1" color="gray">
                        No se detectó texto
                      </Text>
                    )}
                  </Box>
                )}
              </Flex>
            )
          })}
        </div>
      </Flex>

      {/* Validation Modal */}
      <Dialog.Root open={showValidation} onOpenChange={setShowValidation}>
        <Dialog.Content maxWidth="500px">
          <Dialog.Title>Validar Datos Legales</Dialog.Title>
          <Dialog.Description size="2" mb="4">
            Revise los datos extraídos del documento. Puede editarlos si es necesario. Estos datos se guardarán en la
            transferencia.
          </Dialog.Description>

          <Flex direction="column" gap="3">
            <label>
              <Text as="div" size="2" mb="1" weight="bold">
                Tipo de Documento
              </Text>
              <TextField.Root
                value={validationData.documentType}
                onChange={(e) => setValidationData({ ...validationData, documentType: e.target.value })}
                placeholder="Ej: Escritura Pública"
              />
              {documentAnalysis?.powerDocument?.extractedData?.extractionContext?.typeSource && (
                <Text size="1" color="gray" className="mt-1 block">
                  Texto origen: "{documentAnalysis.powerDocument.extractedData.extractionContext.typeSource}"
                </Text>
              )}
            </label>

            <label>
              <Text as="div" size="2" mb="1" weight="bold">
                Fecha Escritura (DD-MM-YYYY)
              </Text>
              <TextField.Root
                value={validationData.powerDate}
                onChange={(e) => setValidationData({ ...validationData, powerDate: e.target.value })}
                placeholder="Ej: 01-01-2023"
              />
              {documentAnalysis?.powerDocument?.extractedData?.extractionContext?.dateSource && (
                <Text size="1" color="gray" className="mt-1 block">
                  Texto origen: "{documentAnalysis.powerDocument.extractedData.extractionContext.dateSource}"
                </Text>
              )}
            </label>

            <label>
              <Text as="div" size="2" mb="1" weight="bold">
                Nombre Notario
              </Text>
              <TextField.Root
                value={validationData.notaryName}
                onChange={(e) => setValidationData({ ...validationData, notaryName: e.target.value })}
              />
              {documentAnalysis?.powerDocument?.extractedData?.extractionContext?.notarySource && (
                <Text size="1" color="gray" className="mt-1 block">
                  Texto origen: "{documentAnalysis.powerDocument.extractedData.extractionContext.notarySource}"
                </Text>
              )}
            </label>

            <label>
              <Text as="div" size="2" mb="1" weight="bold">
                Notaría / Oficina
              </Text>
              <TextField.Root
                value={validationData.notaryOffice}
                onChange={(e) => setValidationData({ ...validationData, notaryOffice: e.target.value })}
              />
              {documentAnalysis?.powerDocument?.extractedData?.extractionContext?.officeSource && (
                <Text size="1" color="gray" className="mt-1 block">
                  Texto origen: "{documentAnalysis.powerDocument.extractedData.extractionContext.officeSource}"
                </Text>
              )}
            </label>
          </Flex>

          <Flex gap="3" mt="4" justify="end">
            <Dialog.Close>
              <Button variant="soft" color="gray">
                Cancelar
              </Button>
            </Dialog.Close>
            <Button onClick={handleSaveValidation} disabled={savingValidation}>
              {savingValidation ? 'Guardando...' : 'Confirmar y Guardar'}
            </Button>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>
      {/* Multi-Upload Modal */}
      <Dialog.Root open={showMultiModal} onOpenChange={setShowMultiModal}>
        <Dialog.Content maxWidth="500px">
          <Dialog.Title>Subir Documento Legal</Dialog.Title>
          <Dialog.Description size="2" mb="4">
            Por favor, suba Constitución empresa y certificado de vigencia.
          </Dialog.Description>

          <Flex direction="column" gap="4">
            {expectedFiles && (
              <Flex direction="column" gap="3">
                <Box className="p-3 border rounded-md">
                  <Text size="2" weight="bold" mb="2" as="div">
                    Archivo 1
                  </Text>
                  {stagedFile1 ? (
                    <Flex align="center" gap="2">
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                      <Text size="2" className="flex-1 truncate">
                        {stagedFile1.name}
                      </Text>
                      <Button variant="ghost" size="1" color="red" onClick={() => setStagedFile1(null)}>
                        <XCircle className="w-4 h-4" />
                      </Button>
                    </Flex>
                  ) : (
                    <label className="cursor-pointer block text-center p-4 border-2 border-dashed rounded bg-gray-50 hover:bg-gray-100">
                      <Upload className="w-6 h-6 mx-auto text-gray-400 mb-2" />
                      <Text size="2" color="blue">
                        Seleccionar archivo
                      </Text>
                      <input
                        type="file"
                        className="hidden"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={(e) => {
                          if (e.target.files?.[0]) setStagedFile1(e.target.files[0])
                        }}
                      />
                    </label>
                  )}
                </Box>

                {expectedFiles === 2 && (
                  <Box className="p-3 border rounded-md">
                    <Text size="2" weight="bold" mb="2" as="div">
                      Archivo 2
                    </Text>
                    {stagedFile2 ? (
                      <Flex align="center" gap="2">
                        <CheckCircle2 className="w-4 h-4 text-green-500" />
                        <Text size="2" className="flex-1 truncate">
                          {stagedFile2.name}
                        </Text>
                        <Button variant="ghost" size="1" color="red" onClick={() => setStagedFile2(null)}>
                          <XCircle className="w-4 h-4" />
                        </Button>
                      </Flex>
                    ) : (
                      <label className="cursor-pointer block text-center p-4 border-2 border-dashed rounded bg-gray-50 hover:bg-gray-100">
                        <Upload className="w-6 h-6 mx-auto text-gray-400 mb-2" />
                        <Text size="2" color="blue">
                          Seleccionar archivo
                        </Text>
                        <input
                          type="file"
                          className="hidden"
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={(e) => {
                            if (e.target.files?.[0]) setStagedFile2(e.target.files[0])
                          }}
                        />
                      </label>
                    )}
                  </Box>
                )}
              </Flex>
            )}

            <Flex gap="3" mt="4" justify="end">
              <Button variant="soft" color="gray" onClick={() => setShowMultiModal(false)}>
                Cancelar
              </Button>
              <Button
                onClick={handleConfirmMultiUpload}
                disabled={
                  !expectedFiles ||
                  (expectedFiles === 1 && !stagedFile1) ||
                  (expectedFiles === 2 && (!stagedFile1 || !stagedFile2))
                }
              >
                Confirmar y Subir
              </Button>
            </Flex>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>
    </Card>
  )
}
